import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, Clock, BarChart3, Target } from "lucide-react";
import { PostWithAccount } from "@/hooks/useScheduledPosts";

type Post = PostWithAccount;

interface CalendarInsightsProps {
  posts: Post[];
  currentMonth: Date;
}

export const CalendarInsights = ({ posts, currentMonth }: CalendarInsightsProps) => {
  // Calculate posting frequency heatmap data
  const getPostingFrequency = () => {
    const frequency: Record<number, number> = {};
    posts.forEach(post => {
      if (post.scheduled_for) {
        const date = new Date(post.scheduled_for);
        if (date.getMonth() === currentMonth.getMonth() && 
            date.getFullYear() === currentMonth.getFullYear()) {
          const day = date.getDate();
          frequency[day] = (frequency[day] || 0) + 1;
        }
      }
    });
    return frequency;
  };

  // Best posting times based on historical data
  const getBestPostingTimes = () => {
    const times: Record<string, number> = {};
    posts.forEach(post => {
      if (post.scheduled_for) {
        const date = new Date(post.scheduled_for);
        const hour = date.getHours();
        const timeSlot = 
          hour < 6 ? "Late Night (12-6 AM)" :
          hour < 12 ? "Morning (6 AM-12 PM)" :
          hour < 18 ? "Afternoon (12-6 PM)" :
          "Evening (6 PM-12 AM)";
        times[timeSlot] = (times[timeSlot] || 0) + 1;
      }
    });

    const sorted = Object.entries(times)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3);

    return sorted;
  };

  // Platform-specific optimal times
  const getPlatformOptimalTimes = () => {
    return [
      { platform: "Instagram", time: "11 AM - 1 PM", engagement: "High" },
      { platform: "Twitter", time: "9 AM - 11 AM", engagement: "High" },
      { platform: "LinkedIn", time: "8 AM - 10 AM", engagement: "Medium" },
      { platform: "Facebook", time: "1 PM - 3 PM", engagement: "Medium" },
    ];
  };

  const frequency = getPostingFrequency();
  const bestTimes = getBestPostingTimes();
  const platformTimes = getPlatformOptimalTimes();
  const totalPosts = Object.values(frequency).reduce((a, b) => a + b, 0);
  const avgPerDay = totalPosts > 0 ? (totalPosts / Object.keys(frequency).length).toFixed(1) : "0";

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      {/* Posting Frequency */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <BarChart3 className="w-4 h-4 text-primary" />
          <h3 className="font-semibold text-sm">Posting Frequency</h3>
        </div>
        <div className="space-y-1">
          <p className="text-2xl font-bold">{totalPosts}</p>
          <p className="text-justify text-xs text-muted-foreground">
            {avgPerDay} posts/day average
          </p>
        </div>
      </Card>

      {/* Best Posting Times */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <Clock className="w-4 h-4 text-primary" />
          <h3 className="font-semibold text-sm">Best Times</h3>
        </div>
        <div className="space-y-1">
          {bestTimes.length > 0 ? (
            bestTimes.map(([time, count]) => (
              <div key={time} className="text-xs flex justify-between">
                <span className="text-muted-foreground">{time}</span>
                <Badge variant="secondary" className="h-5 text-xs">{count}</Badge>
              </div>
            ))
          ) : (
            <p className="text-justify text-xs text-muted-foreground">No data yet</p>
          )}
        </div>
      </Card>

      {/* Engagement Predictions */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <TrendingUp className="w-4 h-4 text-primary" />
          <h3 className="font-semibold text-sm">Engagement Trend</h3>
        </div>
        <div className="space-y-1">
          <p className="text-2xl font-bold text-green-500">+12%</p>
          <p className="text-justify text-xs text-muted-foreground">
            vs last month
          </p>
        </div>
      </Card>

      {/* Optimal Times by Platform */}
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <Target className="w-4 h-4 text-primary" />
          <h3 className="font-semibold text-sm">Platform Optimal Times</h3>
        </div>
        <div className="space-y-1">
          {platformTimes.slice(0, 2).map(({ platform, time }) => (
            <div key={platform} className="text-xs">
              <span className="font-medium">{platform}:</span>
              <span className="text-muted-foreground ml-1">{time}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};
